import PropTypes from 'prop-types';

import { loadReCaptcha } from 'react-recaptcha-v3';

import React from 'react';
import has from 'lodash/has';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import set from 'lodash/set';
import classNames from 'classnames';
import gettext from '@eb/gettext';

import { NotificationBar } from '@eb/eds-notification';
import { withMainControls } from '@eb/eds-structure';

import { ICON_TYPE_PROP_TYPE } from '@eb/eds-icon';
import { TYPE_ERROR } from '@eb/eds-notification';
import {
    SOCIAL_EXTERNAL_IDENTITY_PROVIDER_APPLE,
    SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK,
} from '@eb/social-login-facebook';

import PageHeader from '../../components/PageHeader';
import LookUpForm from '../../components/forms/LookUpForm';
import SignUpForm from '../../components/forms/SignUpForm';
import SocialSignUpForm from '../../components/forms/SocialSignUpForm';
import LogInForm from '../../components/forms/LogInForm';
import LinkAccountsLogInForm from '../../components/forms/LinkAccountsLogInForm';
import LinkedSoftAccountsForm from '../../components/forms/LinkedSoftAccountsForm';

import {
    BASE_URL,
    ACCOUNT_STATUS,
    LOG_IN_PATH,
    SIGN_UP_PATH,
    SIGN_UP_FORM,
    SOCIAL_LOG_IN_PATH,
    SOCIAL_SIGN_UP_PATH,
    LINK_ACCOUNTS_LOG_IN_PATH,
    SET_LINKED_SOFT_ACCOUNTS_PATH,
    SOCIAL_LINK_LOGIN_PATH,
    INVALID_RESET_TOKEN,
} from '../../constants';
import { PAGE_HEADER_INFO_MAP } from '../../constants/messages';

import { isLoginProviderEnabled, normalizePath } from '../../utils';
import { setSocialMocks } from '../../utils/socialMocks';

import { AlertChunky } from '@eb/eds-iconography';

const FORM_MAP = {
    [ACCOUNT_STATUS]: LookUpForm,
    [LOG_IN_PATH]: LogInForm,
    [SIGN_UP_PATH]: SignUpForm,
    [SOCIAL_SIGN_UP_PATH]: SocialSignUpForm,
    [LINK_ACCOUNTS_LOG_IN_PATH]: LinkAccountsLogInForm,
    [SET_LINKED_SOFT_ACCOUNTS_PATH]: LinkedSoftAccountsForm,
    [SOCIAL_LINK_LOGIN_PATH]: LogInForm,
    [SOCIAL_LOG_IN_PATH]: LogInForm,
};

class SignInScreen extends React.PureComponent {
    static propTypes = {
        onLookupUser: PropTypes.func,
        onSignUpUser: PropTypes.func,
        onLogInUser: PropTypes.func,
        onSocialLogInUser: PropTypes.func,
        onLinkAccountsOnLogin: PropTypes.func,
        onLinkedAccountsCreatePassword: PropTypes.func,
        onSocialLogInUserNotAuthorized: PropTypes.func,
        onSocialLogInUserAuthorized: PropTypes.func,
        onTermsOfServiceClick: PropTypes.func,
        onComunityGuidelinesClick: PropTypes.func,
        onPrivacyPolicyClick: PropTypes.func,
        onForceStld: PropTypes.func,
        email: PropTypes.string,
        locale: PropTypes.string,
        handleForgotPassword: PropTypes.func,
        moveBack: PropTypes.func,
        onLoginInsteadClick: PropTypes.func,
        trackEmailConfirmationEdit: PropTypes.func,
        trackEmailMismatchShowed: PropTypes.func,
        clearAuthNotification: PropTypes.func,
        recaptcha2PublicKey: PropTypes.string,
        recaptcha3PublicKey: PropTypes.string,
        disableRecaptcha3: PropTypes.bool,
        showCaptcha: PropTypes.bool,
        fp: PropTypes.bool,
        onSocialProviderClick: PropTypes.func,
        socialSSOSettings: PropTypes.shape({
            facebook: PropTypes.object,
        }),
        authNotificationOptions: PropTypes.shape({
            message: PropTypes.node,
            type: PropTypes.string,
            iconType: ICON_TYPE_PROP_TYPE,
        }),
        shouldRenderAuthNotification: PropTypes.bool,
        linkedAccountSuccessUrl: PropTypes.string,
        trackSIWA_LookUpSuccessWithEmail: PropTypes.func,
        trackSIWA_LookUpSuccessWithHiddenEmail: PropTypes.func,
        socialData: PropTypes.shape({
            accessToken: PropTypes.string,
            sourceProvider: PropTypes.string,
            email: PropTypes.string,
            firstName: PropTypes.string,
            lastName: PropTypes.string,
        }),
        skipSocialCalls: PropTypes.bool,
    };

    state = {
        invalidPasswordResetToken: false,
        shouldAnimate: false,
    };

    componentDidMount() {
        const {
            router: {
                location: { query },
            },
        } = this.props;

        /*This is needed here because the route is redirected on the server, before we have the
        chance to set state. SO once the component is loaded we can determine if this page was
        redirected to due to an invalid reset password token*/
        if (query[INVALID_RESET_TOKEN]) {
            //eslint-disable-next-line react/no-did-mount-set-state
            this.setState({ invalidPasswordResetToken: true });
        }

        if (!this.props.disableRecaptcha3) {
            loadReCaptcha(this.props.recaptcha3PublicKey);
        }
    }

    componentWillReceiveProps({ location: { pathname } }) {
        const { route } = this.props;

        //Needed so that the submit button correctly animates up when the
        //user navigates to the /signin, but not on initial load.
        if (pathname) {
            //In certain cases the pathname is /signin/ rather than /signin
            //due to this we want to normalize the path to /signin
            const normalizedPath = normalizePath(pathname);

            //This conditional checks that the route we are going TO is the base page,
            //but the route we are currently on is a nested route (/login or /signup etc)
            if (normalizedPath === BASE_URL && has(route, 'path')) {
                this.setState({ shouldAnimate: true });
            }
        }
    }

    _getSubmitAction() {
        const {
            onLookupUser,
            onLinkAccountsOnLogin,
            onLinkedAccountsCreatePassword,
            onLogInUser,
            onSignUpUser,
            onSocialSignUpUser,
        } = this.props;
        const formType = this._getRoutePath();

        const actionMap = {
            [ACCOUNT_STATUS]: onLookupUser,
            [LOG_IN_PATH]: onLogInUser,
            [LINK_ACCOUNTS_LOG_IN_PATH]: onLinkAccountsOnLogin,
            [SIGN_UP_PATH]: onSignUpUser,
            [SOCIAL_SIGN_UP_PATH]: onSocialSignUpUser,
            [SET_LINKED_SOFT_ACCOUNTS_PATH]: onLinkedAccountsCreatePassword,
            [SOCIAL_LINK_LOGIN_PATH]: onLinkAccountsOnLogin,
            [SOCIAL_LOG_IN_PATH]: onLinkAccountsOnLogin,
        };

        return actionMap[formType];
    }

    _handleSubmit = (formData) => {
        const { clearAuthNotification } = this.props;
        const action = this._getSubmitAction();
        const formType = this._getRoutePath();

        // clear notification everytime form is submitted
        if (clearAuthNotification) {
            clearAuthNotification();
        }

        return action(formData);
    };

    _handleOnSocialLogIn = (socialProvider, socialResponse) => {
        const {
            clearAuthNotification,
            onSocialLogInUser,
            socialSSOSettings,
        } = this.props;

        // clear notification everytime form is submitted
        if (clearAuthNotification) {
            clearAuthNotification();
        }

        // add param to call api, depending on feature flag
        let skipCreation = false;

        if (socialSSOSettings) {
            skipCreation = socialSSOSettings.twoStepsSignup || false;
        }

        if (onSocialLogInUser) {
            onSocialLogInUser(socialProvider, {
                skipCreation,
                ...socialResponse,
            });
        }
    };

    _handleOnSocialLogInNotAuthorized = (socialProvider, socialStatus) => {
        const { onSocialLogInUserNotAuthorized } = this.props;

        if (onSocialLogInUserNotAuthorized) {
            onSocialLogInUserNotAuthorized(socialProvider, socialStatus);
        }
    };

    _handleOnSocialProviderClick = async (
        socialProvider,
        stateFromProvider,
    ) => {
        const {
            onSocialProviderClick,
            onSkipSocialLinks,
            skipSocialCalls,
        } = this.props;

        await setSocialMocks({
            socialProvider,
            // Social mocking props
            onSkipSocialLinks,
            skipSocialCalls,
        });

        if (onSocialProviderClick) {
            onSocialProviderClick({
                socialProvider,
                stateFromProvider,
            });
        }
    };

    _handleOnGetStartedClick = () => {
        const { onGetStartedClick } = this.props;

        if (onGetStartedClick) {
            onGetStartedClick();
        }
    };

    _handleTermsOfServiceClick = () => {
        const { onTermsOfServiceClick } = this.props;

        if (onTermsOfServiceClick) {
            onTermsOfServiceClick();
        }
    };

    _handleComunityGuidelinesClick = () => {
        const { onComunityGuidelinesClick } = this.props;

        if (onComunityGuidelinesClick) {
            onComunityGuidelinesClick();
        }
    };

    _handlePrivacyPolicyClick = () => {
        const { onPrivacyPolicyClick } = this.props;

        if (onPrivacyPolicyClick) {
            onPrivacyPolicyClick();
        }
    };

    _getRoutePath() {
        let formType = ACCOUNT_STATUS;
        const { route } = this.props;

        if (has(route, 'path')) {
            formType = route.path;
        }

        return formType;
    }

    _handleShowNotification(error) {
        const { addMainNotification } = this.props;

        /*Limit the call to addMainNotification to only if
        a notification hasn't been shown since last submit.*/
        if (!this._hasNotification) {
            addMainNotification({
                type: TYPE_ERROR,
                children: error,
                iconType: <AlertChunky />,
            });

            this._hasNotification = true;
        }
    }

    _renderAuthNotification = () => {
        const {
            authNotificationOptions,
            shouldRenderAuthNotification,
        } = this.props;

        let component = null;

        if (shouldRenderAuthNotification) {
            const { message, ...props } = authNotificationOptions;

            component = (
                <div className="eds-l-mar-top-4 eds-l-mar-bot-8 sign-in-screen__notification-container">
                    <NotificationBar {...props}>{message}</NotificationBar>
                </div>
            );
        }

        return component;
    };

    _handleClearNotification() {
        const { hideMainNotification } = this.props;

        this._hasNotification = false;
        hideMainNotification && hideMainNotification();
    }

    render() {
        const {
            form,
            handleForgotPassword,
            locale,
            recaptcha2PublicKey,
            recaptcha3PublicKey,
            showCaptcha,
            email,
            moveBack,
            onLoginInsteadClick,
            fp,
            socialSSOSettings,
            shouldRenderAuthNotification,
            linkedAccountSuccessUrl,
            disableRecaptcha3,
            onForceStld,
            trackEmailConfirmationEdit,
            trackEmailMismatchShowed,
            socialAuthenticationData,
            trackSIWA_LookUpSuccessWithEmail,
            trackSIWA_LookUpSuccessWithHiddenEmail,
            clearAuthNotification,
            handleSendMailLinkAccount,
            onSocialLogInUserAuthorized,
            socialData: { sourceProvider },
        } = this.props;

        const { invalidPasswordResetToken, shouldAnimate } = this.state;
        const formType = this._getRoutePath();
        const DynamicForm = FORM_MAP[formType];
        let syncErrors;
        let trackOnPageLoad;
        let extraForm = '';
        if (!isEmpty(socialAuthenticationData.apple)) {
            trackOnPageLoad =
                socialAuthenticationData.apple &&
                socialAuthenticationData.apple.isPrivateEmail
                    ? trackSIWA_LookUpSuccessWithHiddenEmail
                    : trackSIWA_LookUpSuccessWithEmail;
            // For apple link account, change header
            if (formType === LINK_ACCOUNTS_LOG_IN_PATH) {
                extraForm = 'Apple';
            }
        }

        let { buttonCTA, ...pageHeaderProps } = PAGE_HEADER_INFO_MAP[
            `${formType}${extraForm}Form`
        ];

        // replace subtitle if only Facebook integration is enabled and we are on the first screen
        if (
            formType === ACCOUNT_STATUS &&
            isLoginProviderEnabled(
                socialSSOSettings,
                SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK,
            ) &&
            !isLoginProviderEnabled(
                socialSSOSettings,
                SOCIAL_EXTERNAL_IDENTITY_PROVIDER_APPLE,
            )
        ) {
            pageHeaderProps = {
                ...pageHeaderProps,
                subtitleText: gettext('Use Facebook or email to get started.'),
            };
        }

        if (formType === SIGN_UP_PATH && form[SIGN_UP_FORM]) {
            syncErrors = form[SIGN_UP_FORM].syncErrors;
        }

        // hide subtitle when an auth notification is being displayed
        if (shouldRenderAuthNotification) {
            pageHeaderProps = omit(pageHeaderProps, 'subtitleText');
        }

        const gridClasses = classNames(
            'eds-g-cell',
            'container-max-width',
            'eds-g-cell--has-overflow',
        );

        return (
            <div className="eds-l-mar-vert-14">
                <div className="eds-g-group eds-align--center">
                    <div className={gridClasses}>
                        <PageHeader {...pageHeaderProps} />

                        {this._renderAuthNotification()}

                        <DynamicForm
                            formErrors={syncErrors}
                            buttonCTA={buttonCTA}
                            formType={formType}
                            onSubmit={this._handleSubmit}
                            onSocialConnectVerified={this._handleOnSocialLogIn}
                            onSocialConnectNotAuthorized={
                                this._handleOnSocialLogInNotAuthorized
                            }
                            onSocialLogInUserAuthorized={
                                onSocialLogInUserAuthorized
                            }
                            onSocialProviderClick={
                                this._handleOnSocialProviderClick
                            }
                            onGetStartedClick={this._handleOnGetStartedClick}
                            onTermsOfServiceClick={
                                this._handleTermsOfServiceClick
                            }
                            onComunityGuidelinesClick={
                                this._handleComunityGuidelinesClick
                            }
                            onPrivacyPolicyClick={
                                this._handlePrivacyPolicyClick
                            }
                            onForceStld={onForceStld}
                            onLoginInsteadClick={onLoginInsteadClick}
                            clearAuthNotification={clearAuthNotification}
                            handleEmailEdit={moveBack}
                            handleForgotPassword={handleForgotPassword}
                            handleSendMailLinkAccount={
                                handleSendMailLinkAccount
                            }
                            locale={locale}
                            recaptcha2PublicKey={recaptcha2PublicKey}
                            recaptcha3PublicKey={recaptcha3PublicKey}
                            disableRecaptcha3={disableRecaptcha3}
                            showCaptcha={showCaptcha}
                            email={email}
                            invalidPasswordResetToken={
                                invalidPasswordResetToken
                            }
                            fp={fp}
                            showNotification={this._handleShowNotification.bind(
                                this,
                            )}
                            clearNotification={this._handleClearNotification.bind(
                                this,
                            )}
                            shouldAnimate={shouldAnimate}
                            socialSSOSettings={socialSSOSettings}
                            socialAuthenticationData={socialAuthenticationData}
                            linkedAccountSuccessUrl={linkedAccountSuccessUrl}
                            trackEmailConfirmationEdit={
                                trackEmailConfirmationEdit
                            }
                            trackEmailMismatchShowed={trackEmailMismatchShowed}
                            trackOnPageLoad={trackOnPageLoad}
                            sourceProvider={sourceProvider}
                        />
                    </div>
                </div>
            </div>
        );
    }
}

export default withMainControls(SignInScreen);
